import React, { useState, useEffect, useRef } from 'react';

import AttentionDeficitImg from '../assets/Children Sleep Apnea/Attention Deficit.svg';
import SnoringImg from '../assets/Children Sleep Apnea/Snoring.svg';
import MouthBreathingImg from '../assets/Children Sleep Apnea/Mouth Breathing.svg';
import PoorSchoolImg from '../assets/Children Sleep Apnea/Poor School Performance.svg';
import FatigueImg from '../assets/Children Sleep Apnea/Fatigue.svg';
import BruxismImg from '../assets/Children Sleep Apnea/Bruxism _ Teeth Grinding.svg';
import BedWettingImg from '../assets/Children Sleep Apnea/Bed Wetting.svg';
import CrookedTeethImg from '../assets/Children Sleep Apnea/Crooked Teeth.svg';
import RestlessSleepImg from '../assets/Children Sleep Apnea/Restless Sleep.svg';
import NightSweatsImg from '../assets/Children Sleep Apnea/Night Sweats.svg';
import FrequentAllergiesImg from '../assets/Children Sleep Apnea/Frequent Allergies.svg';
import PickyEatingImg from '../assets/Children Sleep Apnea/Picky Eating.svg';

const signs = [
  {
    image: AttentionDeficitImg,
    title: 'Attention Deficit',
    description:
      'Poor oxygen flow during sleep impairs brain development and focus. Children with sleep apnea are frequently misdiagnosed with ADHD due to the overlap in inattention and hyperactivity symptoms.',
    fact: 'Up to 25% of ADHD children have sleep apnea',
  },
  {
    image: SnoringImg,
    title: 'Snoring',
    description:
      'Snoring in children is never normal. It signals a partially blocked airway and is one of the most reliable early indicators of sleep-disordered breathing that needs evaluation.',
    fact: 'Habitual snoring affects 10–12% of children',
  },
  {
    image: MouthBreathingImg,
    title: 'Mouth Breathing',
    description:
      'When nasal passages are restricted, children default to mouth breathing. This alters jaw development, promotes narrow palates, and perpetuates the very airway narrowing causing the problem.',
    fact: 'Mouth breathing reshapes jaw & facial structure',
  },
  {
    image: PoorSchoolImg,
    title: 'Poor School Performance',
    description:
      'Sleep deprivation from nightly breathing interruptions impairs memory consolidation, problem-solving, and learning. Children with untreated sleep apnea consistently underperform academically.',
    fact: 'Sleep apnea linked to lower IQ test scores',
  },
  {
    image: FatigueImg,
    title: 'Fatigue',
    description:
      'Despite sleeping a full night, children with sleep apnea never reach deep, restorative sleep stages. This results in persistent daytime tiredness, low energy, and irritability throughout the day.',
    fact: 'Fatigue is the #1 daytime complaint in OSA',
  },
  {
    image: BruxismImg,
    title: 'Bruxism / Teeth Grinding',
    description:
      'The body grinds teeth as a reflex to reopen a collapsing airway during sleep. Bruxism in children is strongly correlated with sleep-disordered breathing and airway obstruction.',
    fact: 'Bruxism resolves in ~50% after OSA treatment',
  },
  {
    image: BedWettingImg,
    title: 'Bed Wetting',
    description:
      'Oxygen drops from apnea events stimulate the release of atrial natriuretic peptide, a hormone that increases urine production at night. This explains the strong link between bed wetting and sleep apnea.',
    fact: 'OSA treatment reduces bed wetting significantly',
  },
  {
    image: CrookedTeethImg,
    title: 'Crooked Teeth',
    description:
      'Mouth breathing causes the tongue to drop from the palate, removing the natural force that guides jaw widening. The result is a narrow, high-arched palate with crowded, crooked teeth.',
    fact: 'Airway expansion improves tooth alignment',
  },
  {
    image: RestlessSleepImg,
    title: 'Restless Sleep',
    description:
      'Repeated micro-arousals caused by breathing interruptions prevent children from staying in deep sleep. They toss, turn, and adopt unusual sleeping positions in an unconscious attempt to open the airway.',
    fact: 'Restlessness indicates repeated arousals nightly',
  },
  {
    image: NightSweatsImg,
    title: 'Night Sweats',
    description:
      'The physical effort of breathing against a restricted airway and the stress response triggered by low oxygen causes increased body temperature and excessive sweating during sleep.',
    fact: 'Night sweats signal increased respiratory effort',
  },
  {
    image: FrequentAllergiesImg,
    title: 'Frequent Allergies',
    description:
      'Chronic nasal congestion from allergies narrows the already small airways in children, triggering or worsening sleep apnea. Sleep deprivation in turn weakens immune response, increasing allergy severity.',
    fact: 'Allergies & sleep apnea form a feedback loop',
  },
  {
    image: PickyEatingImg,
    title: 'Picky Eating',
    description:
      'Children who mouth breathe often have a heightened gag reflex and altered taste sensitivity. Narrow jaw development from poor airway posture can also make chewing certain food textures uncomfortable.',
    fact: 'Linked to narrow palate & oral muscle dysfunction',
  },
];

const AUTOPLAY_DELAY = 5000;

export default function ChildrenSignsCarousel() {
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressRef = useRef(null);
  const startTimeRef = useRef(null);

  const goTo = (index) => {
    setCurrent((index + signs.length) % signs.length);
    setProgress(0);
    startTimeRef.current = Date.now();
  };

  const next = () => goTo(current + 1);
  const prev = () => goTo(current - 1);

  useEffect(() => {
    startTimeRef.current = Date.now();
    setProgress(0);

    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % signs.length);
      startTimeRef.current = Date.now();
      setProgress(0);
    }, AUTOPLAY_DELAY);

    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setProgress(Math.min((elapsed / AUTOPLAY_DELAY) * 100, 100));
    }, 50);

    return () => {
      clearInterval(timerRef.current);
      clearInterval(progressRef.current);
    };
  }, [current]);

  const slide = signs[current];

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-4xl mx-auto">

        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-4">
            Pediatric Sleep Apnea
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">
            Signs of Sleep Apnea in Children
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Children rarely say "I didn't sleep well." Instead, sleep apnea shows up as behaviour, focus, and growth problems — often mistaken for other conditions.
          </p>
        </div>

        {/* All Signs Thumbnail Grid */}
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 mb-10">
          {signs.map((sign, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`flex flex-col items-center gap-2 p-2 rounded-xl transition-all duration-200 group ${
                i === current
                  ? 'bg-primary/10 ring-2 ring-primary/40'
                  : 'hover:bg-gray-50'
              }`}
            >
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 ${
                  i === current ? 'bg-primary/20' : 'bg-gray-100 group-hover:bg-primary/10'
                }`}
              >
                <img
                  src={sign.image}
                  alt={sign.title}
                  className="w-8 h-8 object-contain"
                />
              </div>
              <span
                className={`text-center leading-tight hidden sm:block transition-colors duration-200 ${
                  i === current
                    ? 'text-primary font-semibold text-xs'
                    : 'text-gray-500 text-xs group-hover:text-secondary'
                }`}
              >
                {sign.title}
              </span>
            </button>
          ))}
        </div>

        {/* Main Card */}
        <div className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden">

          {/* Progress bar */}
          <div className="h-1 bg-gray-200">
            <div
              className="h-1 bg-primary"
              style={{ width: `${progress}%`, transition: 'width 0.05s linear' }}
            />
          </div>

          {/* Slide content */}
          <div className="flex flex-col md:flex-row items-center gap-8 p-8 md:p-12 min-h-[280px]">

            {/* Image */}
            <div className="w-40 h-40 md:w-48 md:h-48 rounded-full bg-white border-4 border-primary/20 flex items-center justify-center flex-shrink-0 shadow-sm">
              <img
                src={slide.image}
                alt={slide.title}
                className="w-28 h-28 md:w-32 md:h-32 object-contain"
              />
            </div>

            {/* Text */}
            <div className="flex flex-col items-center md:items-start text-center md:text-left gap-4 flex-1">

              {/* Counter */}
              <span className="text-xs font-semibold text-primary/60 tracking-widest uppercase">
                Sign {current + 1} of {signs.length}
              </span>

              <h3 className="text-2xl font-bold text-secondary">
                {slide.title}
              </h3>

              <p className="text-gray-600 text-lg leading-relaxed">
                {slide.description}
              </p>

              {/* Fact badge */}
              <div className="inline-block px-5 py-2 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                {slide.fact}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-white">
            <button
              onClick={prev}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:text-secondary hover:border-secondary transition-colors"
              aria-label="Previous sign"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Dot indicators */}
            <div className="flex items-center gap-2 flex-wrap justify-center max-w-xs">
              {signs.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Go to sign ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === current
                      ? 'bg-primary w-6'
                      : 'bg-gray-300 hover:bg-gray-400 w-2'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={next}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:text-secondary hover:border-secondary transition-colors"
              aria-label="Next sign"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}